import { useState, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, AreaChart, Area,
} from 'recharts';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { TrendingUp, MessageSquare, BookOpen, DollarSign, Send, ArrowUpRight, Activity } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const CHANNEL_COLORS = ['#6366f1', '#22c55e'];
const RESERVATION_COLORS = ['#22c55e', '#f43f5e', '#f59e0b'];

function MetricCard({ icon: Icon, label, value, sub, gradient, trend }) {
  return (
    <div style={{
      background: gradient, borderRadius: 18, padding: '22px 24px',
      color: 'white', position: 'relative', overflow: 'hidden',
      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    }}>
      <div style={{ position: 'absolute', top: -15, right: -15, width: 90, height: 90, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', position: 'relative' }}>
        <div>
          <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1 }}>{value}</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', marginTop: 5, fontWeight: 500 }}>{label}</div>
          {sub && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 3 }}>{sub}</div>}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
          <div style={{ width: 42, height: 42, borderRadius: 12, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon size={20} color="white" />
          </div>
          {trend && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 3, background: 'rgba(255,255,255,0.2)', borderRadius: 999, padding: '2px 8px' }}>
              <ArrowUpRight size={12} />
              <span style={{ fontSize: 11, fontWeight: 700 }}>{trend}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1e293b', borderRadius: 10, padding: '10px 14px', boxShadow: '0 8px 24px rgba(0,0,0,0.2)' }}>
      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>
        {(() => { try { return format(new Date(label), 'd MMMM', { locale: tr }); } catch { return label; } })()}
      </div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'white', fontWeight: 600 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: p.color }} />
          {p.name}: {p.value}
        </div>
      ))}
    </div>
  );
};

export default function ReportsPage() {
  const [period, setPeriod] = useState('weekly');
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/reports?period=${period}`)
      .then(setMetrics)
      .catch(() => toast.error('Rapor yüklenemedi'))
      .finally(() => setLoading(false));
  }, [period]);

  const handleSendEmail = async () => {
    setSending(true);
    try {
      const data = await api.post('/reports/send', { period });
      toast.success(data.message);
    } catch (err) {
      toast.error(err.error || 'E-posta gönderilemedi');
    } finally {
      setSending(false);
    }
  };

  const periodLabels = { daily: 'Bugün', weekly: 'Son 7 Gün', monthly: 'Bu Ay' };

  const channelData = metrics ? [
    { name: 'Instagram', value: metrics.instagramMessages },
    { name: 'WhatsApp', value: metrics.whatsappMessages },
  ] : [];

  const reservationData = metrics ? [
    { name: 'Onaylanan', value: metrics.confirmedReservations },
    { name: 'Reddedilen', value: metrics.rejectedReservations },
    { name: 'Bekleyen', value: metrics.pendingReservations },
  ].filter(d => d.value > 0) : [];

  return (
    <div style={{ padding: 28 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', color: '#1e293b' }}>
            Analitik & Raporlar
          </h1>
          <p style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>Performans metrikleri ve trend analizi</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div style={{ display: 'flex', background: 'white', borderRadius: 10, padding: 4, border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
            {['daily', 'weekly', 'monthly'].map(p => (
              <button key={p} onClick={() => setPeriod(p)} style={{
                padding: '6px 16px', border: 'none', borderRadius: 7, fontSize: 13, cursor: 'pointer',
                background: period === p ? 'linear-gradient(135deg, #6366f1, #3b82f6)' : 'transparent',
                color: period === p ? 'white' : '#64748b',
                fontWeight: period === p ? 700 : 500,
                boxShadow: period === p ? '0 2px 8px rgba(99,102,241,0.3)' : 'none',
                transition: 'all 0.2s',
              }}>
                {periodLabels[p]}
              </button>
            ))}
          </div>
          <button className="btn btn-secondary" onClick={handleSendEmail} disabled={sending} style={{ fontSize: 13 }}>
            <Send size={14} /> {sending ? 'Gönderiliyor...' : 'E-posta'}
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, color: '#94a3b8', gap: 10 }}>
          <Activity size={20} color="#6366f1" />
          Analitik yükleniyor...
        </div>
      ) : metrics ? (
        <>
          {/* Metric Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 18, marginBottom: 24 }}>
            <MetricCard icon={MessageSquare} label="Toplam Mesaj" value={metrics.totalMessages} gradient="linear-gradient(135deg, #6366f1, #818cf8)" />
            <MetricCard icon={TrendingUp} label="Yeni Konuşma" value={metrics.newConversations} sub={`%${metrics.conversionRate} dönüşüm`} gradient="linear-gradient(135deg, #3b82f6, #06b6d4)" />
            <MetricCard icon={BookOpen} label="Rezervasyon" value={metrics.totalReservations} sub={`${metrics.confirmedReservations} onaylandı`} gradient="linear-gradient(135deg, #22c55e, #10b981)" />
            <MetricCard icon={DollarSign} label="Tahmini Gelir" value={new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(metrics.totalRevenue)} gradient="linear-gradient(135deg, #f59e0b, #f97316)" />
          </div>

          {/* Charts Row */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 20 }}>
            {/* Area Chart */}
            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                  <h2 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>Mesaj & Rezervasyon Trendi</h2>
                  <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{periodLabels[period]} için günlük akış</p>
                </div>
                <div style={{ display: 'flex', gap: 14 }}>
                  {[{ label: 'Mesaj', color: '#6366f1' }, { label: 'Rezervasyon', color: '#22c55e' }].map(l => (
                    <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#64748b' }}>
                      <div style={{ width: 10, height: 3, borderRadius: 2, background: l.color }} />
                      {l.label}
                    </div>
                  ))}
                </div>
              </div>
              {metrics.dailyTrend?.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={metrics.dailyTrend} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorMsg" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.15} />
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorRes" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#22c55e" stopOpacity={0.15} />
                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }}
                      tickFormatter={d => { try { return format(new Date(d), 'd MMM', { locale: tr }); } catch { return d; } }}
                      axisLine={false} tickLine={false}
                    />
                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="messages" stroke="#6366f1" strokeWidth={2.5} fill="url(#colorMsg)" name="Mesaj" dot={false} />
                    <Area type="monotone" dataKey="reservations" stroke="#22c55e" strokeWidth={2.5} fill="url(#colorRes)" name="Rezervasyon" dot={{ r: 3, fill: '#22c55e' }} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 14 }}>
                  Bu dönem için veri yok
                </div>
              )}
            </div>

            {/* Donut Charts */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="card" style={{ flex: 1 }}>
                <h2 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: '#1e293b' }}>Kanal Dağılımı</h2>
                {channelData.some(d => d.value > 0) ? (
                  <ResponsiveContainer width="100%" height={120}>
                    <PieChart>
                      <Pie data={channelData} cx="50%" cy="50%" innerRadius={33} outerRadius={52} dataKey="value" paddingAngle={4}>
                        {channelData.map((_, i) => <Cell key={i} fill={CHANNEL_COLORS[i]} />)}
                      </Pie>
                      <Tooltip />
                      <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 13 }}>Veri yok</div>
                )}
              </div>
              <div className="card" style={{ flex: 1 }}>
                <h2 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: '#1e293b' }}>Rezervasyon Durumu</h2>
                {reservationData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={120}>
                    <PieChart>
                      <Pie data={reservationData} cx="50%" cy="50%" innerRadius={33} outerRadius={52} dataKey="value" paddingAngle={4}>
                        {reservationData.map((_, i) => <Cell key={i} fill={RESERVATION_COLORS[i]} />)}
                      </Pie>
                      <Tooltip />
                      <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 13 }}>Veri yok</div>
                )}
              </div>
            </div>
          </div>

          {/* Detailed Metrics */}
          <div className="card">
            <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 18, color: '#1e293b' }}>Detaylı Metrikler</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
              {[
                { label: 'Bot Dönüşüm Oranı', value: `%${metrics.conversionRate}`, sub: 'Mesaj → Rezervasyon', color: '#6366f1', bg: '#ede9fe' },
                { label: 'Instagram Mesajları', value: metrics.instagramMessages, sub: `Toplam %${metrics.totalMessages > 0 ? Math.round(metrics.instagramMessages / metrics.totalMessages * 100) : 0}`, color: '#3b82f6', bg: '#dbeafe' },
                { label: 'WhatsApp Mesajları', value: metrics.whatsappMessages, sub: `Toplam %${metrics.totalMessages > 0 ? Math.round(metrics.whatsappMessages / metrics.totalMessages * 100) : 0}`, color: '#22c55e', bg: '#dcfce7' },
                { label: 'Onaylanan Rezervasyon', value: metrics.confirmedReservations, sub: metrics.totalReservations > 0 ? `%${Math.round(metrics.confirmedReservations / metrics.totalReservations * 100)} onay oranı` : '—', color: '#10b981', bg: '#d1fae5' },
                { label: 'Reddedilen Rezervasyon', value: metrics.rejectedReservations, sub: 'Ret edildi', color: '#f43f5e', bg: '#fee2e2' },
                { label: 'Bekleyen Rezervasyon', value: metrics.pendingReservations, sub: 'Yanıt bekleniyor', color: '#f59e0b', bg: '#fef3c7' },
              ].map(m => (
                <div key={m.label} style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  background: m.bg, borderRadius: 12, padding: '14px 18px',
                }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: m.color, minWidth: 50 }}>{m.value}</div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{m.label}</div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{m.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
